// Provides incident utility functions
import { titleCase } from "@tod-ui/helpers/strings";

export function incidentAuthorizedStatus(incident) {
  if (!incident) return "";
  if (incident?.resolution === "claimed") return "claimed";

  if (
    isMultiUnit(incident) &&
    !["closed", "resolved"].includes(incident?.status)
  ) {
    return "ongoing";
  }

  if (incident?.status === "resolved" && incident?.resolution === "surveyed") {
    let survey;
    if (typeof incident?.survey === "string") {
      try {
        survey = JSON.parse(incident?.survey || "{}");
      } catch (e) {
        survey = {};
      }
    } else {
      survey = incident?.survey || {};
    }
    if (
      survey?.authorizationStatus === "authorized" ||
      survey?.vacantUnitSurveyReason == "unit_cleaning" ||
      survey?.vacantUnitSurveyReason == "prospective_tenant"
    ) {
      return "resolved-surveyed";
    } else {
      return "un-resolved-surveyed";
    }
  }

  if (
    incident?.status === "resolved" &&
    incident?.resolution === "presence_confirmed"
  )
    return "presence-confirmed";

  if (incident?.type === "unconfirmed_access") return "presence-unconfirmed";

  if (incident?.status === "closed") {
    if (
      incident?.resolution === "denied" ||
      incident?.hasDisabledLienResponder ||
      incident?.type === "facility_motion"
    ) {
      return "unauthorized";
    }

    if (incident?.isVacant) return "closed-vacant";
    if (incident?.queuedUntilDawn) return "unexplained-after-hours";
    if (incident?.resolution === "unexplained" && !incident?.duringAccessHours)
      return "closed-unexplained-after-access-hours";
    if (incident?.resolution === "unexplained") return "closed-unexplained";
  }
  if (incident?.resolution === "denied") return "possible-unauthorized";

  if (incident?.resolution === "unexplained" && incident?.queuedUntilDawn)
    return "unexplained-after-hours";

  if (
    incident?.resolution === "unexplained" &&
    (incident?.status === "started" || incident?.status === "muted")
  )
    return incident?.hasDisabledLienResponder ||
      incident?.type === "facility_motion"
      ? "unauthorized"
      : "ongoing";
  return "";
}

export function incidentStatus(incident) {
  if (incident.type === "entry") {
    return incident.closedAt ? `Door Closed` : "Door Opened";
  }
  if (incident.type === "water") {
    return incident.closedAt ? `Water Undetected` : "Water Detected";
  }
  switch (incidentAuthorizedStatus(incident)) {
    case "claimed":
      return "Authorized by Renter";
    case "presence-confirmed":
      return accessStatus("presence_confirmed");
    case "presence-unconfirmed":
      return accessStatus("unconfirmed_access");
    case "resolved-surveyed":
      return "Authorized and Inspected";
    case "un-resolved-surveyed":
      return "Unauthorized and Inspected";
    case "unauthorized":
      return incident?.status === "closed"
        ? "Unauthorized and Not Inspected"
        : `Ongoing ${formatIncidentType(incident)}`;
    case "unexplained-after-hours":
      return "Unauthorized, Renter notified during normal hours";
    case "closed-vacant":
      return "Ended";
    case "closed-unexplained-after-access-hours":
      return "Unauthorized, after hours motion";
    case "closed-unexplained":
      const isZoneLevel =
        !incident?.organization?.subtype ||
        incident?.organization?.subtype !== "unit";
      return isZoneLevel ? "Ended" : "Ended, No Response from Renter";
    case "possible-unauthorized":
      return "Possible Unauthorized";
    case "ongoing":
      return `Ongoing ${formatIncidentType(incident)}`;
  }
  return formatIncidentType(incident);
}

export function formatIncidentType(incident) {
  if (["humidity", "temperature", "service_request"].includes(incident.type))
    return titleCase(incident.type);
  if (incident.type === "water") return "Water Detected";
  if (incident.type === "entry") return "Door Open";
  if (incident.type === "unconfirmed_access") return "Access Code w/o Motion";

  if (isMultiUnit(incident)) return "Multi-Unit Motion";

  let prefix = undefined;
  if (incident.type === "motion")
    prefix = incident.queuedUntilDawn === true ? "After Hours" : "Active";
  if (incident.isVacant && incident.extendedDuration) {
    prefix =
      incident.duringAccessHours === true
        ? "Day Time "
        : incident.duringAccessHours === false
        ? "After Hours "
        : "";
    prefix += "Extended";
  }
  if (incident.hasDisabledLienResponder) prefix = "Disabled-Overlocked";

  let suffix = undefined;
  if (incident?.resolution === "presence_confirmed") {
    suffix = "w/ Access Code";
  } else if (incident?.bypassedAccess && !incident.isVacant) {
    suffix = "w/o Access Code";
  }

  return [prefix, titleCase(incident.type || incident), suffix]
    .join(" ")
    .trim();
}

export function isMultiUnit(incident) {
  return incident?.type === "facility_motion";
}

export function accessStatus(type) {
  switch (type) {
    case "presence_confirmed":
      return "Pre Authorized Unit Entry";

    case "unconfirmed_access":
      return "Access Code w/o Unit Entry";

    case "bypassed_access":
      return "Unit Entry w/o Access Code";

    default:
      break;
  }
}

export function incidentDisplayType(type) {
  switch (type.toLowerCase()) {
    case "motion":
      return "Motion";
    case "entry":
    case "Door":
      return "Door";
    case "facility_motion":
    case "facility motion":
      return "Zone Motion";
    default:
      return type;
  }
}

// The incident types the Customer UI offers when routing responder alerts, in
// the order the picker shows them. Narrower than the backend's full set: door,
// service request, battery and signal strength are not facility hardware a
// customer manager routes alerts for.
export const RESPONDER_INCIDENT_TYPES = [
  "all",
  "motion",
  "facility_motion",
  "temperature",
  "humidity",
];

// Incident types that only exist where smart zones are installed.
export const ZONE_INCIDENT_TYPES = ["facility_motion"];

// Responder-facing labels. "motion" is a single unit's own sensor, so it is
// named for the hardware the manager recognises rather than the raw value.
const RESPONDER_INCIDENT_TYPE_LABELS = {
  all: "All",
  motion: "Unit Motion",
  facility_motion: "Zone Motion",
  temperature: "Temperature",
  humidity: "Humidity",
};

export function responderIncidentTypeLabel(type) {
  return RESPONDER_INCIDENT_TYPE_LABELS[type] || incidentDisplayType(type);
}

// Picker options built from the backend's valid types, so a type the backend
// drops disappears here too. Ordered by RESPONDER_INCIDENT_TYPES rather than the
// backend's alphabetical sort. Zone types are withheld unless the target scope
// has zones installed.
export function responderIncidentTypeOptions(
  validTypes,
  { zonesEnabled } = {}
) {
  return (validTypes || [])
    .filter(({ value }) => RESPONDER_INCIDENT_TYPES.includes(value))
    .filter(({ value }) => zonesEnabled || !ZONE_INCIDENT_TYPES.includes(value))
    .sort(
      (a, b) =>
        RESPONDER_INCIDENT_TYPES.indexOf(a.value) -
        RESPONDER_INCIDENT_TYPES.indexOf(b.value)
    )
    .map(({ value }) => ({ value, text: responderIncidentTypeLabel(value) }));
}

export function getTaskStatus(task) {
  if (!task) return "No Task";

  const now = new Date();
  const dueDate = task.dueAt ? new Date(task.dueAt) : null;

  if (dueDate && dueDate < now) {
    return "Overdue";
  }

  if (task.resolution) {
    return task.resolution;
  }

  return "In Progress";
}

export function calculateIncidentDuration(incident) {
  if (!incident.startedAt) return "N/A";

  const endedAt =
    incident.lastUntriggeredAt || incident.closedAt || incident.resolvedAt;

  const startDate = new Date(incident.startedAt);
  const endDate = endedAt ? new Date(endedAt) : new Date();

  const durationMs = endDate - startDate;

  const days = Math.floor(durationMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (durationMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) {
    return `${days} day(s) ${hours} hour(s) ${minutes} minute(s)`;
  } else if (hours > 0) {
    return `${hours} hour(s) ${minutes} minute(s)`;
  } else {
    return `${minutes} minute(s)`;
  }
}
